import { useEffect, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";

const empty = {
  title: "", subtitle: "", description: "", category: "AWS", vendor: "Amazon Web Services",
  difficulty: "Intermediate", price: 19.99, original_price: 39.99, image_url: "",
  bestseller: false, new_launch: false, recently_updated: true,
  what_you_learn: [], topics: [], test_count: 6, question_count: 390,
  duration_minutes: 130, pass_score: 75, faq: [],
};

export default function AdminCourses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/courses?limit=100");
      setItems(data);
    } catch (e) {
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(empty);
    setOpen(true);
  };

  const startEdit = (course) => {
    setEditingId(course.id);
    setForm({
      ...empty,
      ...course,
      what_you_learn: (course.what_you_learn || []).join("\n"),
      topics: (course.topics || []).join(", "),
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        original_price: parseFloat(form.original_price),
        test_count: parseInt(form.test_count),
        question_count: parseInt(form.question_count),
        duration_minutes: parseInt(form.duration_minutes),
        pass_score: parseInt(form.pass_score),
        what_you_learn: typeof form.what_you_learn === "string" ? form.what_you_learn.split("\n").filter(Boolean) : form.what_you_learn,
        topics: typeof form.topics === "string" ? form.topics.split(",").map((t) => t.trim()).filter(Boolean) : form.topics,
      };
      if (editingId) {
        await api.put(`/admin/courses/${editingId}`, payload);
        toast.success("Course updated");
      } else {
        await api.post("/admin/courses", payload);
        toast.success("Course created");
      }
      setOpen(false); setEditingId(null); setForm(empty); load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this course?")) return;
    await api.delete(`/admin/courses/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-courses-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Courses</h1>
          <p className="text-muted-foreground text-sm">Manage all certification practice courses.</p>
        </div>
        <button onClick={startCreate} className="btn-primary" data-testid="add-course-button">
          <Plus className="h-4 w-4" /> New course
        </button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="admin-courses-table">
        <div className="grid grid-cols-[1fr_120px_100px_80px_90px] gap-3 px-5 py-3 text-[11px] uppercase font-bold tracking-wider border-b border-border text-muted-foreground">
          <div>Title</div><div>Vendor</div><div>Price</div><div>Tests</div><div></div>
        </div>
        {loading && (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="admin-courses-loading">Loading courses…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground">No courses yet. Create your first one.</div>
        )}
        {!loading && items.map((c) => (
          <div key={c.id} className="grid grid-cols-[1fr_120px_100px_80px_90px] gap-3 px-5 py-4 border-b border-border last:border-0 items-center" data-testid={`admin-course-row-${c.slug}`}>
            <div className="flex items-center gap-3 min-w-0">
              <img src={c.image_url} alt="" className="h-10 w-14 rounded object-cover" />
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{c.title}</div>
                <div className="text-xs text-muted-foreground">{c.category} · {c.difficulty}</div>
              </div>
            </div>
            <div className="text-sm">{c.vendor}</div>
            <div className="text-sm">${c.price.toFixed(2)}</div>
            <div className="text-sm">{c.test_count}</div>
            <div className="text-right flex justify-end gap-1">
              <button onClick={() => startEdit(c)} className="text-foreground/70 hover:bg-accent rounded p-1.5" data-testid={`edit-course-${c.slug}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => del(c.id)} className="text-rose-500 hover:bg-rose-500/10 rounded p-1.5" data-testid={`delete-course-${c.slug}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-6">
          <div className="bg-background rounded-xl border border-border max-w-2xl w-full p-6 my-8" data-testid="course-create-modal">
            <h2 className="font-display text-2xl mb-4">{editingId ? "Edit course" : "New course"}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Title" v={form.title} on={(v) => setForm({ ...form, title: v })} />
              <Field label="Vendor" v={form.vendor} on={(v) => setForm({ ...form, vendor: v })} />
              <Field label="Category" v={form.category} on={(v) => setForm({ ...form, category: v })} />
              <Field label="Difficulty" v={form.difficulty} on={(v) => setForm({ ...form, difficulty: v })} />
              <Field label="Subtitle" v={form.subtitle} on={(v) => setForm({ ...form, subtitle: v })} colSpan />
              <Field label="Image URL" v={form.image_url} on={(v) => setForm({ ...form, image_url: v })} colSpan />
              <Field label="Price ($)" type="number" v={form.price} on={(v) => setForm({ ...form, price: v })} />
              <Field label="Original price ($)" type="number" v={form.original_price} on={(v) => setForm({ ...form, original_price: v })} />
              <Field label="Test count" type="number" v={form.test_count} on={(v) => setForm({ ...form, test_count: v })} />
              <Field label="Question count" type="number" v={form.question_count} on={(v) => setForm({ ...form, question_count: v })} />
              <Field label="Duration (min)" type="number" v={form.duration_minutes} on={(v) => setForm({ ...form, duration_minutes: v })} />
              <Field label="Pass score (%)" type="number" v={form.pass_score} on={(v) => setForm({ ...form, pass_score: v })} />
              <label className="text-sm flex items-center gap-2 rounded-md border border-border px-3 py-2">
                <input type="checkbox" checked={form.bestseller} onChange={(e) => setForm({ ...form, bestseller: e.target.checked })} data-testid="course-bestseller-input" /> Bestseller
              </label>
              <label className="text-sm flex items-center gap-2 rounded-md border border-border px-3 py-2">
                <input type="checkbox" checked={form.new_launch} onChange={(e) => setForm({ ...form, new_launch: e.target.checked })} data-testid="course-new-launch-input" /> New launch
              </label>
              <Field label="What you learn (one per line)" textarea v={Array.isArray(form.what_you_learn) ? form.what_you_learn.join("\n") : form.what_you_learn} on={(v) => setForm({ ...form, what_you_learn: v })} colSpan />
              <Field label="Topics (comma-separated)" v={Array.isArray(form.topics) ? form.topics.join(", ") : form.topics} on={(v) => setForm({ ...form, topics: v })} colSpan />
            </div>
            <div className="mt-6 flex gap-2 justify-end">
              <button onClick={() => { setOpen(false); setEditingId(null); }} className="btn-ghost" data-testid="cancel-course-button">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="save-course-button">{editingId ? "Update course" : "Create course"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, on, type = "text", textarea, colSpan }) {
  return (
    <label className={`text-sm ${colSpan ? "sm:col-span-2" : ""}`}>
      <div className="text-[11px] uppercase font-bold tracking-wider text-muted-foreground mb-1">{label}</div>
      {textarea ? (
        <textarea rows={4} value={v} onChange={(e) => on(e.target.value)} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground" />
      ) : (
        <input type={type} value={v} onChange={(e) => on(e.target.value)} className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-foreground" />
      )}
    </label>
  );
}
